/**
 * ngn-checkout.js
 *
 * Intercepts the cart checkout button. When the active display currency
 * is NGN, it POSTs to the NGN checkout service, which creates a session
 * and returns a URL to the hosted checkout page. The browser is then
 * redirected.
 *
 * When the active currency is USD, the button is left alone and Shopify's
 * native checkout proceeds as normal.
 *
 * NOTE: The customer's email and shipping address are collected on the
 * hosted checkout page (/checkout). This script does NOT prompt for them.
 *
 * EVENT DELEGATION:
 * The cart drawer re-renders its contents on every open, which destroys
 * and recreates the #checkout button. Attaching a listener directly to
 * the button would fail on the first click after a re-render. Instead,
 * we attach a single delegated listener to `document` that catches
 * clicks on #checkout regardless of when the button was created.
 */

(function () {
  'use strict';

  const CHECKOUT_SERVICE_URL = window.MA_NGN_CHECKOUT_SERVICE_URL || null;

  if (!CHECKOUT_SERVICE_URL) {
    console.warn('[ngn-checkout] MA_NGN_CHECKOUT_SERVICE_URL not set');
    return;
  }

  function getCurrentCurrency() {
    return document.documentElement.getAttribute('data-ma-currency') || 'USD';
  }

  async function handleCheckout(event, button) {
    // Bail early if USD is active — let Shopify's native checkout run.
    if (getCurrentCurrency() !== 'NGN') {
      return;
    }

    // NGN is active — take over the click.
    event.preventDefault();

    button.disabled = true;

    try {
      const lineItems = collectLineItems();
      if (lineItems.length === 0) {
        throw new Error('Cart is empty');
      }

      const sessionRes = await fetch(`${CHECKOUT_SERVICE_URL}/api/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lineItems,
          cartId: getCartId(),
          discountCodes: getDiscountCodes(),
        }),
      });

      if (!sessionRes.ok) {
        // Read the error body so we can log it
        let errorBody = 'Unknown error';
        try {
          const errorJson = await sessionRes.json();
          errorBody = JSON.stringify(errorJson);
        } catch {
          errorBody = await sessionRes.text();
        }
        console.error(
          `[ngn-checkout] Session creation failed (${sessionRes.status}):`,
          errorBody
        );
        throw new Error(`Session creation failed: ${sessionRes.status}`);
      }

      const { checkoutUrl } = await sessionRes.json();
      window.location.href = checkoutUrl;
    } catch (err) {
      console.error('[ngn-checkout]', err);
      button.disabled = false;

      // Surface a useful message if we can identify the cause
      let message = 'Could not start NGN checkout. Please try again.';
      if (err instanceof TypeError && err.message.includes('Failed to fetch')) {
        message =
          'Could not reach the checkout service. Please check your connection.';
      } else if (err.message === 'Cart is empty') {
        message = 'Your cart is empty.';
      } else if (err.message.includes('500')) {
        message =
          'Checkout service is temporarily unavailable. Please try again in a moment.';
      }

      alert(message);
    }
  }

  function collectLineItems() {
    const items = document.querySelectorAll('[data-ma-cart-item]');
    const result = [];

    items.forEach((el) => {
      const variantId = el.getAttribute('data-variant-id');
      const quantity = parseInt(el.getAttribute('data-quantity'), 10);

      if (!variantId || variantId.trim() === '') {
        console.warn(
          '[ngn-checkout] Cart row is missing data-variant-id. ' +
            "The theme's cart-products.liquid needs to be updated. " +
            'Row HTML:',
          el.outerHTML.substring(0, 300)
        );
        return;
      }

      if (!quantity || quantity < 1) {
        console.warn('[ngn-checkout] Cart row has invalid quantity:', el);
        return;
      }

      result.push({ variantId, quantity });
    });

    return result;
  }

  function getCartId() {
    return window.MA_CART_ID || '';
  }

  function getDiscountCodes() {
    const pills = document.querySelectorAll('[data-discount-code]');
    return Array.from(pills).map((p) => p.getAttribute('data-discount-code'));
  }

  // -------------------------------------------------------------------------
  // EVENT DELEGATION
  //
  // A single listener on `document` handles all clicks that originate from
  // (or bubble through) the checkout button. This survives cart-drawer
  // re-renders, currency toggles, and any timing where the button is
  // recreated between page load and click.
  // -------------------------------------------------------------------------
  document.addEventListener('click', function (event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const checkoutButton = target.closest('#checkout');
    if (!checkoutButton) return;

    // Fire the async handler without blocking the event.
    // handleCheckout calls preventDefault() itself when taking over.
    handleCheckout(event, checkoutButton);
  });
})();